import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Search, MessageCircle, Mail, Phone, Eye, Archive, ArchiveRestore,
  StickyNote, Send, Loader2, Inbox, Upload, FileImage, Download, X,
  FileText, CalendarClock, ShieldCheck, Pencil, Factory, DollarSign,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/adminApi";
import { validateRut, validateEmail, validateCelular, formatRut } from "@/lib/validation";
import { SUB_COBERTURAS, SUB_COBERTURA_SHORT, EMAILS_ENABLED } from "@/constants/site";
import { getPartnerByCode } from "@/config/partners";

export const LEAD_STATUS = [
  { value: "nuevo", label: "Nuevo", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "simulacion_lista", label: "En cotización", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  { value: "simulacion_enviada", label: "Simulación enviada", cls: "bg-violet-100 text-violet-700 border-violet-200" },
  { value: "en_emision", label: "En emisión", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "ganado", label: "Emitido", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "perdido", label: "Perdido", cls: "bg-rose-100 text-rose-700 border-rose-200" },
];
const LEGACY_STATUS = {
  contactado: { label: "Contactado (antiguo)", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  cotizado: { label: "Cotizado (antiguo)", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const statusCls = (s) => LEAD_STATUS.find((x) => x.value === s)?.cls || LEGACY_STATUS[s]?.cls || "bg-gray-100 text-gray-600";
const statusLabel = (s) => LEAD_STATUS.find((x) => x.value === s)?.label || LEGACY_STATUS[s]?.label || s;

export const INDUSTRIA_LABELS = {
  maquinaria: "Maquinaria",
  equipos: "Equipos",
  plantas: "Plantas",
  infraestructura: "Ingeniería",
  flotas: "Flotas",
  transporte: "Transporte",
  responsabilidad_civil: "Responsabilidad Civil",
  colaboradores: "Colaboradores",
  fiel_cumplimiento: "Garantías",
};

const CANAL_META = {
  formulario: { label: "Formulario", cls: "bg-slate-100 text-slate-600" },
  whatsapp: { label: "WhatsApp", cls: "bg-emerald-100 text-emerald-700" },
  mail: { label: "Mail", cls: "bg-blue-100 text-blue-700" },
  referido: { label: "Referido", cls: "bg-purple-100 text-purple-700" },
  sitio_partner: { label: "Sitio del partner", cls: "bg-slate-900 text-white" },
};
const CanalBadge = ({ canal }) => {
  if (!canal || canal === "formulario") return null;
  const m = CANAL_META[canal] || { label: canal, cls: "bg-slate-100 text-slate-600" };
  return (
    <span data-testid={`canal-badge-${canal}`} className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${m.cls}`}>
      {m.label}
    </span>
  );
};

const PartnerBadge = ({ partner }) => {
  if (!partner) return null;
  const p = getPartnerByCode(partner);
  return (
    <span
      data-testid={`partner-badge-${partner}`}
      className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${p?.badgeClass || "border-slate-200 bg-slate-100 text-slate-700"}`}
      title={`Llegó desde el sitio de ${p?.name || partner}`}
    >
      ★ {p?.name || partner}
    </span>
  );
};

const IndustriaBadges = ({ items }) => (
  <div className="flex flex-wrap gap-1">
    {(items || []).map((k) => (
      <span key={k} className="inline-flex items-center rounded-full bg-brand-yellow/25 text-brand-ink px-2 py-0.5 text-[10px] font-semibold">
        {INDUSTRIA_LABELS[k] || k}
      </span>
    ))}
  </div>
);

const SubBadges = ({ items }) => (
  <div className="flex flex-wrap gap-1">
    {(items || []).map((k) => (
      <span key={k} className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold">
        {SUB_COBERTURA_SHORT[k] || k}
      </span>
    ))}
  </div>
);

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso || "-"; }
};
const fmtCLP = (n) => (n == null ? "" : `$${Number(n).toLocaleString("es-CL")}`);

const waLink = (celular, nombre) => {
  const digits = (celular || "").replace(/\D/g, "");
  const norm = digits.startsWith("56") ? digits : `56${digits}`;
  const name = (nombre || "").split(" ")[0];
  const text = encodeURIComponent(`Hola${name ? ` ${name}` : ""}, te contacto desde OperaSafe.cl por tu cotización de seguro.`);
  return `https://wa.me/${norm}?text=${text}`;
};

export function LeadsManager({ leads, onChange }) {
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notesFor, setNotesFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [contratarFor, setContratarFor] = useState(null);
  const [commissionFor, setCommissionFor] = useState(null);
  const [busy, setBusy] = useState({});

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (!showArchived && l.archived) return false;
      if (showArchived && !l.archived) return false;
      if (!term) return true;
      const hay = [l.nombre, l.rut, l.email, l.celular, l.equipo_descripcion, ...(l.tipo_industria || []), ...(l.sub_coberturas || [])].join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [leads, q, showArchived]);

  const setBusyFor = (id, key, v) => setBusy((b) => ({ ...b, [id]: { ...(b[id] || {}), [key]: v } }));
  const isBusy = (id, key) => Boolean(busy?.[id]?.[key]);

  const changeStatus = async (l, status) => {
    setBusyFor(l.id, "status", true);
    try { await api.patch(`/admin/leads/${l.id}/status`, { status }); toast.success("Estado actualizado"); onChange?.(); }
    catch { toast.error("No se pudo"); }
    finally { setBusyFor(l.id, "status", false); }
  };
  const setAseguradora = async (l, aseguradora) => {
    try { await api.patch(`/admin/leads/${l.id}/aseguradora`, { aseguradora }); toast.success("Aseguradora guardada"); onChange?.(); }
    catch { toast.error("No se pudo"); }
  };
  const uploadCotizacion = async (l, file) => {
    setBusyFor(l.id, "cot", true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/leads/${l.id}/cotizacion`, fd); toast.success("Cotización subida"); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error al subir"); }
    finally { setBusyFor(l.id, "cot", false); }
  };
  const deleteCotizacion = async (l) => {
    if (!confirm("¿Eliminar la cotización de este lead?")) return;
    try { await api.delete(`/admin/leads/${l.id}/cotizacion`); toast.success("Cotización eliminada"); onChange?.(); }
    catch { toast.error("No se pudo"); }
  };
  const sendQuote = async (l) => {
    if (!(l.aseguradora || "").trim()) return toast.error("Ingresa la aseguradora ganadora antes de enviar");
    setBusyFor(l.id, "quote", true);
    try { await api.post(`/admin/leads/${l.id}/send-quote`); toast.success("Cotización enviada por email"); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error al enviar"); }
    finally { setBusyFor(l.id, "quote", false); }
  };
  const sendPoliza = async (l) => {
    setBusyFor(l.id, "poliza", true);
    try { await api.post(`/admin/leads/${l.id}/send-poliza`); toast.success("Póliza enviada por email"); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error al enviar"); }
    finally { setBusyFor(l.id, "poliza", false); }
  };
  const archive = async (l, archived) => {
    try { await api.patch(`/admin/leads/${l.id}/archive`, { archived }); toast.success(archived ? "Archivado" : "Restaurado"); onChange?.(); }
    catch { toast.error("No se pudo"); }
  };

  // Fila de acciones, compartida por la tabla (desktop) y las tarjetas (mobile).
  const renderActions = (l, sfx = "") => (
    <div className="flex flex-wrap gap-1">
      <label className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs cursor-pointer ${l.cotizacion ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-gray-200 hover:border-brand-yellow"}`}
        title={l.cotizacion ? "Cambiar cotización" : "Subir cotización"}>
        {isBusy(l.id, "cot") ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileImage className="h-3.5 w-3.5" />}
        <span className="md:hidden xl:inline">Cotización</span>
        <input type="file" accept="image/*,application/pdf" hidden data-testid={`lead-cot-upload-${l.id}${sfx}`}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCotizacion(l, f); e.target.value = ""; }} />
      </label>
      {l.cotizacion && (
        <Button size="icon" variant="outline" onClick={() => deleteCotizacion(l)} data-testid={`lead-cot-delete-${l.id}${sfx}`} title="Eliminar cotización">
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
      {EMAILS_ENABLED && (
        <Button size="icon" variant="outline" onClick={() => sendQuote(l)} disabled={isBusy(l.id, "quote")} data-testid={`lead-send-quote-${l.id}${sfx}`} title="Enviar cotización por email">
          {isBusy(l.id, "quote") ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        </Button>
      )}
      <Button size="icon" variant="outline" onClick={() => setContratarFor(l)} data-testid={`lead-contratar-${l.id}${sfx}`} title={l.poliza ? "Ver póliza contratada" : "Contratar"}>
        <ShieldCheck className={`h-3.5 w-3.5 ${l.poliza ? "text-emerald-600" : ""}`} />
      </Button>
      {l.poliza && EMAILS_ENABLED && (
        <Button size="icon" variant="outline" onClick={() => sendPoliza(l)} disabled={isBusy(l.id, "poliza")} data-testid={`lead-send-poliza-${l.id}${sfx}`} title="Enviar póliza al cliente">
          {isBusy(l.id, "poliza") ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
        </Button>
      )}
      <Button size="icon" variant="outline" onClick={() => setCommissionFor(l)} data-testid={`lead-commission-${l.id}${sfx}`} title="Editar comisión / monto">
        <DollarSign className="h-3.5 w-3.5" />
      </Button>
      <Button size="icon" variant="outline" onClick={() => setNotesFor(l)} data-testid={`lead-notes-${l.id}${sfx}`} title="Notas">
        <StickyNote className="h-3.5 w-3.5" />
        {(l.notes?.length || 0) > 0 && <span className="ml-0.5 text-[10px] font-bold">{l.notes.length}</span>}
      </Button>
      <Button size="icon" variant="outline" onClick={() => setSelected(l)} data-testid={`lead-view-${l.id}${sfx}`} title="Ver detalle">
        <Eye className="h-3.5 w-3.5" />
      </Button>
      <Button size="icon" variant="outline" onClick={() => setEditFor(l)} data-testid={`lead-edit-${l.id}${sfx}`} title="Editar">
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Button size="icon" variant="outline" onClick={() => archive(l, !l.archived)} data-testid={`lead-archive-${l.id}${sfx}`} title={l.archived ? "Restaurar" : "Archivar"}>
        {l.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, RUT, email, producto..." className="pl-9" />
        </div>
        <div className="flex gap-2">
          <Button variant={showArchived ? "default" : "outline"} size="sm" onClick={() => setShowArchived((s) => !s)} data-testid="toggle-archived">
            {showArchived ? <><ArchiveRestore className="h-4 w-4 mr-1" /> Ver activos</> : <><Archive className="h-4 w-4 mr-1" /> Ver archivados</>}
          </Button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Inbox className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          {showArchived ? "Sin leads archivados." : "Aún no hay leads."}
        </div>
      ) : (
        <>
        {/* Mobile: tarjetas (la tabla no se puede leer en un celular) */}
        <div className="space-y-3 md:hidden" data-testid="leads-cards-mobile">
          {filtered.map((l) => (
            <div
              key={l.id}
              data-testid={`lead-card-${l.id}`}
              className={`rounded-2xl border border-gray-200 bg-white p-4 shadow-sm ${l.archived ? "opacity-70" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-brand-ink">{l.nombre || "-"}</p>
                  <p className="text-xs text-gray-500">{l.rut}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-[11px] text-gray-500">{fmtDate(l.created_at)}</p>
                  <div className="mt-0.5 flex flex-wrap justify-end gap-1">
                    <PartnerBadge partner={l.partner} />
                    <CanalBadge canal={l.canal} />
                  </div>
                </div>
              </div>

              <div className="mt-2.5"><IndustriaBadges items={l.tipo_industria} /></div>

              <div className="mt-3 flex gap-2">
                <Select value={l.status} onValueChange={(v) => changeStatus(l, v)}>
                  <SelectTrigger data-testid={`lead-status-${l.id}-m`} className={`h-11 flex-1 font-semibold border ${statusCls(l.status)}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  data-testid={`lead-aseguradora-${l.id}-m`}
                  className="h-11 w-28 text-xs"
                  defaultValue={l.aseguradora || ""}
                  onBlur={(e) => e.target.value !== (l.aseguradora || "") && setAseguradora(l, e.target.value)}
                  placeholder="Aseguradora"
                />
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2">
                <a href={`tel:${l.celular}`} data-testid={`lead-call-${l.id}-m`} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700">
                  <Phone className="h-4 w-4" /> Llamar
                </a>
                <a href={waLink(l.celular, l.nombre)} target="_blank" rel="noreferrer" data-testid={`lead-wa-${l.id}-m`} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 text-xs font-semibold text-emerald-700">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
                <a href={`mailto:${l.email}`} data-testid={`lead-mail-${l.id}-m`} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700">
                  <Mail className="h-4 w-4" /> Email
                </a>
              </div>

              <div className="mt-3 border-t border-gray-100 pt-3">{renderActions(l, "-m")}</div>
            </div>
          ))}
        </div>

        {/* Desktop: tabla completa */}
        <div className="hidden overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm md:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Producto(s)</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Aseguradora</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Contacto</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} className={l.archived ? "opacity-70" : ""}>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {fmtDate(l.created_at)}
                    {l.ref && <div className="mt-0.5 text-[10px] font-bold text-purple-700">Ref: {l.ref}</div>}
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      <PartnerBadge partner={l.partner} />
                      <CanalBadge canal={l.canal} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-brand-ink">{l.nombre || "-"}</div>
                    <div className="text-xs text-gray-500">{l.rut}</div>
                    {l.equipo_descripcion && <div className="text-[11px] text-gray-500 mt-0.5 max-w-xs truncate" title={l.equipo_descripcion}>{l.equipo_descripcion}</div>}
                  </td>
                  <td className="px-4 py-3"><IndustriaBadges items={l.tipo_industria} /></td>
                  <td className="px-4 py-3">
                    <Select value={l.status} onValueChange={(v) => changeStatus(l, v)}>
                      <SelectTrigger data-testid={`lead-status-${l.id}`} className={`h-8 w-40 font-semibold border ${statusCls(l.status)}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEAD_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {l.aseguradora && (
                      <div className="text-[10px] text-gray-500 mt-1">
                        <ShieldCheck className="h-3 w-3 inline text-emerald-500" /> {l.aseguradora}
                        {l.poliza_vence && <span className="ml-1">· vence {l.poliza_vence}</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Input
                      data-testid={`lead-aseguradora-${l.id}`}
                      className="h-8 w-32 text-xs"
                      defaultValue={l.aseguradora || ""}
                      onBlur={(e) => e.target.value !== (l.aseguradora || "") && setAseguradora(l, e.target.value)}
                      placeholder="BCI / Sura..."
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5 text-xs">
                      <a href={`mailto:${l.email}`} className="text-brand-ink hover:text-brand-gold inline-flex items-center gap-1"><Mail className="h-3 w-3" />{l.email}</a>
                      <a href={`tel:${l.celular}`} className="text-gray-600 inline-flex items-center gap-1"><Phone className="h-3 w-3" />{l.celular}</a>
                      <a href={waLink(l.celular, l.nombre)} target="_blank" rel="noreferrer" className="text-emerald-700 inline-flex items-center gap-1"><MessageCircle className="h-3 w-3" />WhatsApp</a>
                    </div>
                  </td>
                  <td className="px-4 py-3">{renderActions(l)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Detail sheet */}
      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="sm:max-w-lg w-full overflow-y-auto">
          {selected && <LeadDetail lead={selected} />}
        </SheetContent>
      </Sheet>

      {/* Notes dialog */}
      {notesFor && (
        <NotesDialog lead={notesFor} onClose={() => setNotesFor(null)} onSaved={onChange} />
      )}
      {/* Edit dialog */}
      {editFor && (
        <EditDialog lead={editFor} onClose={() => setEditFor(null)} onSaved={onChange} />
      )}
      {/* Contratar dialog */}
      {contratarFor && (
        <ContratarDialog lead={contratarFor} onClose={() => setContratarFor(null)} onSaved={onChange} />
      )}
      {/* Commission dialog */}
      {commissionFor && (
        <CommissionDialog lead={commissionFor} onClose={() => setCommissionFor(null)} onSaved={onChange} />
      )}
    </div>
  );
}

function LeadDetail({ lead }) {
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2"><Factory className="h-5 w-5 text-brand-gold" /> {lead.nombre}</SheetTitle>
        <SheetDescription>Detalle del lead</SheetDescription>
      </SheetHeader>
      <div className="mt-4 space-y-3 text-sm">
        <Row label="Estado"><span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${statusCls(lead.status)}`}>{statusLabel(lead.status)}</span></Row>
        <Row label="Producto(s)"><IndustriaBadges items={lead.tipo_industria} /></Row>
        {(lead.sub_coberturas || []).length > 0 && (
          <Row label="Coberturas colaboradores"><SubBadges items={lead.sub_coberturas} /></Row>
        )}
        {lead.equipo_descripcion && <Row label="Equipo">{lead.equipo_descripcion}</Row>}
        <Row label="RUT">{lead.rut}</Row>
        <Row label="Email">{lead.email}</Row>
        <Row label="Teléfono">{lead.celular}</Row>
        {lead.aseguradora && <Row label="Aseguradora">{lead.aseguradora}{lead.poliza_vence ? ` (vence ${lead.poliza_vence})` : ""}</Row>}
        {lead.sale_amount != null && <Row label="Monto venta">{fmtCLP(lead.sale_amount)}</Row>}
        {lead.commission_amount != null && <Row label="Comisión">{fmtCLP(lead.commission_amount)} {lead.commission_paid ? "✓ pagada" : "pendiente"}</Row>}
        <Row label="Canal">{CANAL_META[lead.canal]?.label || "-"}</Row>
        <Row label="Origen">
          {lead.partner
            ? `Sitio de ${getPartnerByCode(lead.partner)?.name || lead.partner}`
            : lead.ref
              ? `Referido ${lead.ref}`
              : "Sitio general"}
        </Row>
        {lead.ref && <Row label="Referido">{lead.ref}</Row>}
        <Row label="Creado">{fmtDate(lead.created_at)}</Row>

        {lead.current_policy && (
          <div className="pt-3 border-t border-gray-200">
            <p className="font-semibold text-brand-ink mb-2">
              Póliza actual del cliente
              <span className="ml-2 text-[10px] font-normal text-gray-500">— adjunta al enviar el formulario</span>
            </p>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/file?path=${encodeURIComponent(lead.current_policy.path)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-amber-700 hover:underline">
              <FileText className="h-4 w-4" /> {lead.current_policy.filename}
            </a>
          </div>
        )}
        {lead.cotizacion && (
          <div className="pt-3 border-t border-gray-200">
            <p className="font-semibold text-brand-ink mb-2">Cotización</p>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/file?path=${encodeURIComponent(lead.cotizacion.path)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-gold hover:underline">
              <FileImage className="h-4 w-4" /> {lead.cotizacion.filename}
            </a>
          </div>
        )}
        {lead.poliza && (
          <div className="pt-3 border-t border-gray-200">
            <p className="font-semibold text-brand-ink mb-2">Póliza</p>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/file?path=${encodeURIComponent(lead.poliza.path)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-600 hover:underline">
              <FileText className="h-4 w-4" /> {lead.poliza.filename}
            </a>
          </div>
        )}

        {(lead.notes?.length || 0) > 0 && (
          <div className="pt-3 border-t border-gray-200">
            <p className="font-semibold text-brand-ink mb-2">Historial ({lead.notes.length})</p>
            <ul className="space-y-2">
              {lead.notes.map((n, i) => (
                <li key={i} className="text-xs bg-gray-50 border border-gray-200 rounded-md p-2">
                  <div className="font-semibold text-brand-ink whitespace-pre-wrap">{n.text}</div>
                  <div className="text-gray-500 mt-1">{n.author} · {fmtDate(n.created_at)}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
}

const Row = ({ label, children }) => (
  <div className="grid grid-cols-3 gap-3 items-baseline">
    <span className="text-xs uppercase tracking-wide text-gray-400">{label}</span>
    <div className="col-span-2 text-brand-ink">{children}</div>
  </div>
);

function NotesDialog({ lead, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try { await api.post(`/admin/leads/${lead.id}/notes`, { text }); toast.success("Nota agregada"); setText(""); onSaved?.(); }
    catch { toast.error("No se pudo"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="notes-dialog">
        <DialogHeader>
          <DialogTitle>Notas: {lead.nombre}</DialogTitle>
          <DialogDescription>Historial y nueva nota del lead.</DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto space-y-2 my-3">
          {(lead.notes || []).map((n, i) => (
            <div key={i} className="text-xs bg-gray-50 border border-gray-200 rounded-md p-2">
              <div className="font-semibold text-brand-ink whitespace-pre-wrap">{n.text}</div>
              <div className="text-gray-500 mt-1">{n.author} · {fmtDate(n.created_at)}</div>
            </div>
          ))}
          {(lead.notes || []).length === 0 && <p className="text-sm text-gray-500">Sin notas todavía.</p>}
        </div>
        <div>
          <Label>Nueva nota</Label>
          <Textarea data-testid="note-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Contacto realizado, esperando docs, etc." />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button onClick={submit} disabled={saving || !text.trim()} data-testid="note-save" className="bg-brand-ink hover:bg-brand-inkHover text-white">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Guardando</> : "Agregar nota"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ lead, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre: lead.nombre || "", rut: lead.rut || "", email: lead.email || "",
    celular: lead.celular || "", equipo_descripcion: lead.equipo_descripcion || "",
    tipo_industria: lead.tipo_industria || [],
    sub_coberturas: lead.sub_coberturas || [],
  });
  const [saving, setSaving] = useState(false);
  const toggle = (v) => setForm((f) => {
    const next = f.tipo_industria.includes(v) ? f.tipo_industria.filter((x) => x !== v) : [...f.tipo_industria, v];
    return { ...f, tipo_industria: next, sub_coberturas: next.includes("colaboradores") ? f.sub_coberturas : [] };
  });
  const toggleSub = (v) => setForm((f) => ({ ...f, sub_coberturas: f.sub_coberturas.includes(v) ? f.sub_coberturas.filter((x) => x !== v) : [...f.sub_coberturas, v] }));
  const submit = async () => {
    if (!validateRut(form.rut)) return toast.error("RUT inválido");
    if (!validateEmail(form.email)) return toast.error("Email inválido");
    if (!validateCelular(form.celular)) return toast.error("Teléfono inválido");
    if (form.tipo_industria.length === 0) return toast.error("Al menos un producto");
    setSaving(true);
    try { await api.patch(`/admin/leads/${lead.id}/edit`, form); toast.success("Guardado"); onSaved?.(); onClose?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
        <DialogHeader>
          <DialogTitle>Editar lead</DialogTitle>
          <DialogDescription>Actualiza los datos del cliente y su equipo.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div><Label>Nombre / razón social</Label><Input data-testid="edit-nombre" value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>RUT</Label><Input data-testid="edit-rut" value={form.rut} onChange={(e) => setForm((f) => ({ ...f, rut: formatRut(e.target.value) }))} /></div>
            <div><Label>Teléfono</Label><Input data-testid="edit-celular" value={form.celular} onChange={(e) => setForm((f) => ({ ...f, celular: e.target.value }))} /></div>
          </div>
          <div><Label>Email</Label><Input data-testid="edit-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
          <div>
            <Label>Producto(s)</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {Object.entries(INDUSTRIA_LABELS).map(([k, label]) => (
                <button key={k} type="button" onClick={() => toggle(k)} data-testid={`edit-industria-${k}`}
                  className={`text-left px-3 py-1.5 rounded-lg border-2 text-xs font-medium transition-colors ${form.tipo_industria.includes(k) ? "border-brand-yellow bg-brand-yellow/20 text-brand-ink" : "border-gray-200 bg-white text-gray-600 hover:border-brand-yellow/50"}`}>{label}</button>
              ))}
            </div>
          </div>
          {form.tipo_industria.includes("colaboradores") && (
            <div className="rounded-xl border border-brand-yellow/50 bg-brand-yellow/10 p-3" data-testid="edit-sub-block">
              <Label className="text-[13px]">Coberturas de colaboradores</Label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {SUB_COBERTURAS.map((sub) => {
                  const on = form.sub_coberturas.includes(sub.id);
                  return (
                    <button type="button" key={sub.id} data-testid={`edit-sub-${sub.id}`} onClick={() => toggleSub(sub.id)}
                      className={`rounded-full border px-2.5 py-1 text-[11.5px] font-medium transition-colors ${on ? "border-brand-ink bg-brand-ink text-white" : "border-brand-ink/20 bg-white text-brand-ink hover:border-brand-ink/50"}`}>
                      {sub.name.replace(/^Seguro (de |por )?/i, "")}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div><Label>Equipo / detalle</Label><Textarea rows={2} data-testid="edit-equipo" value={form.equipo_descripcion} onChange={(e) => setForm((f) => ({ ...f, equipo_descripcion: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="edit-save" className="bg-brand-ink hover:bg-brand-inkHover text-white">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Guardando</> : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContratarDialog({ lead, onClose, onSaved }) {
  const [aseguradora, setAseguradora] = useState(lead.aseguradora || "");
  const [vence, setVence] = useState(lead.poliza_vence || "");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!aseguradora.trim()) return toast.error("Ingresa aseguradora");
    if (!vence) return toast.error("Fecha de vencimiento requerida");
    if (!file && !lead.poliza) return toast.error("Sube la póliza");
    setSaving(true);
    try {
      if (file) {
        const fd = new FormData();
        fd.append("aseguradora", aseguradora);
        fd.append("poliza_vence", vence);
        fd.append("file", file);
        await api.post(`/admin/leads/${lead.id}/contratar`, fd);
      } else {
        await api.patch(`/admin/leads/${lead.id}/policy`, { aseguradora, poliza_vence: vence });
      }
      toast.success("Contratación guardada");
      onSaved?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="contratar-dialog">
        <DialogHeader>
          <DialogTitle>{lead.poliza ? "Ver póliza" : "Contratar"}: {lead.nombre}</DialogTitle>
          <DialogDescription>Confirmar aseguradora, vencimiento y adjuntar póliza. Marca el lead como <b>Emitido</b>.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div><Label>Aseguradora</Label><Input data-testid="contratar-aseg" value={aseguradora} onChange={(e) => setAseguradora(e.target.value)} placeholder="BCI Seguros / SURA / ..." /></div>
          <div><Label>Vencimiento póliza</Label><Input data-testid="contratar-vence" type="date" value={vence} onChange={(e) => setVence(e.target.value)} /></div>
          <div>
            <Label>{lead.poliza ? "Reemplazar póliza (opcional)" : "Póliza (PDF / imagen)"}</Label>
            <Input data-testid="contratar-file" type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            {lead.poliza && !file && <p className="text-xs text-gray-500 mt-1">Póliza actual: {lead.poliza.filename}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="contratar-save" className="bg-brand-ink hover:bg-brand-inkHover text-white">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Guardando</> : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CommissionDialog({ lead, onClose, onSaved }) {
  const pct = lead.ref_commission_pct;
  const parentPct = lead.ref_parent_pct;
  const parentCode = lead.ref_parent_code;
  const parentName = lead.ref_parent_name;
  const affName = lead.ref_name;
  const hasParent = !!parentCode;

  const initialSale = lead.sale_amount ?? "";
  const initialComm = lead.commission_amount ?? "";
  const initialParentComm = lead.parent_commission_amount ?? "";
  const [sale, setSale] = useState(initialSale);
  const [comm, setComm] = useState(initialComm);
  const [parentComm, setParentComm] = useState(initialParentComm);
  const [paid, setPaid] = useState(!!lead.commission_paid);
  const [parentPaid, setParentPaid] = useState(!!lead.parent_commission_paid);
  const [saving, setSaving] = useState(false);

  const suggestComm = pct != null && sale !== "" ? Math.round(Number(sale) * Number(pct) / 100) : null;
  const suggestParentComm = parentPct != null && sale !== "" ? Math.round(Number(sale) * Number(parentPct) / 100) : null;

  const applySuggested = (val, setter) => () => { if (val != null) setter(val); };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        sale_amount: sale === "" ? null : Number(sale),
        commission_amount: comm === "" ? null : Number(comm),
        commission_paid: paid,
      };
      if (hasParent) {
        payload.parent_commission_amount = parentComm === "" ? null : Number(parentComm);
        payload.parent_commission_paid = parentPaid;
      }
      await api.patch(`/admin/leads/${lead.id}/commission`, payload);
      toast.success("Guardado");
      onSaved?.(); onClose?.();
    } catch { toast.error("No se pudo"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="commission-dialog">
        <DialogHeader>
          <DialogTitle>Comisión / venta</DialogTitle>
          <DialogDescription>
            Cargá manualmente los montos. El % configurado es sólo referencial.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>Monto venta (CLP)</Label>
            <Input data-testid="comm-sale" type="number" value={sale} onChange={(e) => setSale(e.target.value)} placeholder="1500000" />
          </div>

          {/* Comisión del afiliado (dueño directo) */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="mb-0">
                Comisión afiliado
                {affName && <span className="ml-2 text-xs font-normal text-gray-500">({affName}{pct != null ? ` · ${pct}%` : ""})</span>}
              </Label>
              {suggestComm != null && suggestComm !== Number(comm) && (
                <button type="button" onClick={applySuggested(suggestComm, setComm)}
                  data-testid="comm-use-suggested" className="text-[11px] font-semibold text-brand-gold hover:underline">
                  Usar ${suggestComm.toLocaleString("es-CL")}
                </button>
              )}
            </div>
            <Input data-testid="comm-amount" type="number" value={comm} onChange={(e) => setComm(e.target.value)} placeholder={suggestComm != null ? `Sugerido: ${suggestComm}` : "Comisión (CLP)"} />
            <label className="mt-2 inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" data-testid="comm-paid" checked={paid} onChange={(e) => setPaid(e.target.checked)} className="h-4 w-4" />
              <span className="text-sm">Comisión afiliado pagada</span>
            </label>
          </div>

          {/* Comisión del padre (si aplica) */}
          {hasParent && (
            <div className="rounded-xl border border-brand-yellow/40 bg-brand-yellow/5 p-3" data-testid="parent-comm-block">
              <div className="flex items-center justify-between mb-2">
                <Label className="mb-0">
                  Comisión padre
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    ({parentName || parentCode}{parentPct != null ? ` · ${parentPct}%` : ""})
                  </span>
                </Label>
                {suggestParentComm != null && suggestParentComm !== Number(parentComm) && (
                  <button type="button" onClick={applySuggested(suggestParentComm, setParentComm)}
                    data-testid="parent-comm-use-suggested" className="text-[11px] font-semibold text-brand-gold hover:underline">
                    Usar ${suggestParentComm.toLocaleString("es-CL")}
                  </button>
                )}
              </div>
              <Input data-testid="parent-comm-amount" type="number" value={parentComm} onChange={(e) => setParentComm(e.target.value)} placeholder={suggestParentComm != null ? `Sugerido: ${suggestParentComm}` : "Comisión padre (CLP)"} />
              <label className="mt-2 inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="parent-comm-paid" checked={parentPaid} onChange={(e) => setParentPaid(e.target.checked)} className="h-4 w-4" />
                <span className="text-sm">Comisión padre pagada</span>
              </label>
              <p className="mt-2 text-[11px] text-gray-500">Este monto es lo que le corresponde al padre. Aparece en su portal como su propia comisión sobre este lead.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="comm-save" className="bg-brand-ink hover:bg-brand-inkHover text-white">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Guardando</> : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
